import classNames from 'classnames';
import { Button, Stack, Group, Select, MultiSelect, Text, Collapse, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import LabelWithInformation from '@ui/components/LabelWithInformation/LabelWithInformation';
import Icon from '@ui/components/Icon/Icon';
import FormInput from '../../../../components/FormInput/FormInput';
import DatePicker from '../../../../../components/UI/DatePicker/DatePicker';

import useCreateProjectForm from './useCreateProjectForm';

import './CreateProjectForm.scss';

const ROUND_OPTIONS = [
	{ label: 'Unretouched Images (Start at R0)', value: 'false' },
	{ label: 'Retouched Images (Start at R1)', value: 'true' }
];

type CreateProjectFormProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSubmit: ( data: any ) => void,
	isCreatingProject: boolean,
	initialWorkspaceID?: number,
	onCancelClick?: () => void
};

const CreateProjectForm = ( {
	onSubmit,
	isCreatingProject,
	initialWorkspaceID,
	onCancelClick
}: CreateProjectFormProps ) => {
	const {
		formState: { data },
		errors,
		isValid,
		onNameInputBlur,
		onClientNameInputBlur,
		onFormSubmitted,
		onNameInputChange,
		onClientNameInputChange,
		onStartAtChange,
		onDueDateChange,
		workspaceOptions,
		showWorkspaces,
		onWorkspaceSelectChange,
		onAssetsStartRetouchedSelectChange,
		startDateRef,
		dueDateRef,
		inviteMembersOptions,
		inviteMembersSelected,
		onInviteMembersChange,
		labelGroupsOptions,
		labelGroupsSelected,
		onLabelGroupsChange,
		selectedWorkspace
	} = useCreateProjectForm( { initialWorkspaceID, onSubmit } );

	const [ advanceOptionsOpened, { toggle: toggleAdvanceOptions } ] = useDisclosure( false );

	return (
		<div className={classNames( 'CreateProjectForm', { showWorkspaces } )}>
			<form id="create-project-form" onSubmit={onFormSubmitted} className="form-content">
				<Stack py={20} px={32} spacing={25}>
					<FormInput
						name="name"
						label="Project Name"
						value={data.name}
						error={errors.name}
						onChange={onNameInputChange}
						onBlur={onNameInputBlur}
						required
						placeholder="Enter your project name"
						autoFocus
					/>
					<Select
						name="assetsStartRetouched"
						label={(
							<LabelWithInformation
								text="Where are you starting?"
								tooltipText="Select unretouched or retouched images to determine what review round the project will start on"
								required
							/>
						)}
						aria-label="Where are you starting?"
						value={data.assetsStartRetouched ? ROUND_OPTIONS[ 1 ].value : ROUND_OPTIONS[ 0 ].value}
						data={ROUND_OPTIONS}
						onChange={onAssetsStartRetouchedSelectChange}
						type="tall"
						withAsterisk={false}
						required
						styles={{
							label: { width: '100%', display: 'flex' }
						}}
					/>
					{showWorkspaces
						&& (
							<Select
								name="workspace"
								label={(
									<LabelWithInformation
										text="This project will be created in"
										tooltipText="All projects must belong to a billed workspace. Please select the workspace that will own the project."
										required
									/>
								)}
								aria-label="This project will be created in"
								value={data.workspaceID}
								data={workspaceOptions}
								styles={{
									label: { width: '100%', display: 'flex' }
								}}
								error={errors.workspaceID}
								onChange={onWorkspaceSelectChange}
								type="tall"
								required
								placeholder="Select a workspace"
								data-testid="workspace-selector"
								searchable={false}
								withAsterisk={false}
								maxDropdownHeight={80}
							/>
						)}
					<Box
						onClick={toggleAdvanceOptions}
						data-testid="advanced-options-toggle"
						className="advanced-options-toggle"
					>
						<Group position="apart" align="center">
							<Text weight={500} className="advanced-options-text">Advanced Options</Text>
							<Icon
								icon={advanceOptionsOpened ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
								size={20}
								color="neutral.600"
							/>
						</Group>
					</Box>
					<Collapse in={advanceOptionsOpened}>
						<Stack spacing={25}>
							<FormInput
								name="clientName"
								label="Client Name"
								value={data.clientName}
								error={errors.clientName}
								onChange={onClientNameInputChange}
								onBlur={onClientNameInputBlur}
								placeholder="Enter your client name"
							/>
							<Group className="dates" position="apart">
								<DatePicker
									key="startDate"
									displayText="Start Date"
									name="startDate"
									disabledDays={{
										before: new Date(),
										after: data.dueDate ? new Date( data.dueDate ) : null
									}}
									refHandler={startDateRef}
									value={data.startDate ? new Date( data.startDate ) : null}
									dayChangeHandler={onStartAtChange}
								/>
								<DatePicker
									key="dueDate"
									displayText="End Date"
									name="endDate"
									disabledDays={{
										before: new Date( data.startDate )
									}}
									refHandler={dueDateRef}
									value={data.dueDate ? new Date( data.dueDate ) : null}
									dayChangeHandler={onDueDateChange}
									placeholder="Select end date"
								/>
							</Group>
							<MultiSelect
								name="inviteMembers"
								label={(
									<LabelWithInformation
										text="Collaborator Groups"
										tooltipText="To manage collaborator groups, go to Workspace Settings. You can also invite others at any time by sharing the project."
										feature={PricingPlanFeature.CONTACT_GROUPS}
										selectedWorkspace={selectedWorkspace}
										origin="createProjectForm"
									/>
								)}
								aria-label="Collaborator Groups"
								value={inviteMembersSelected}
								onChange={onInviteMembersChange}
								data={inviteMembersOptions}
								disabled={data.workspaceID === null}
								color="primary"
								placeholder={(
									data.workspaceID === null
										? 'Select a workspace to invite collaborators'
										: 'Add collaborator groups and/or members'
								)}
								withinPortal
								dropdownPosition="flip"
								maxDropdownHeight={300}
								data-testid="collaborators-select"
								styles={{
									label: { width: '100%', display: 'flex' }
								}}
							/>
							<MultiSelect
								name="labelGroups"
								label={(
									<LabelWithInformation
										text="Label Groups"
										tooltipText="To manage label groups, go to Workspace Settings. You can also add other labels at any time inside the project."
										feature={PricingPlanFeature.LABEL_GROUPS}
										selectedWorkspace={selectedWorkspace}
										origin="createProjectForm"
									/>
								)}
								aria-label="Label Groups"
								value={labelGroupsSelected}
								onChange={onLabelGroupsChange}
								data={labelGroupsOptions}
								disabled={data.workspaceID === null}
								color="primary"
								placeholder={(
									data.workspaceID === null
										? 'Select a workspace to choose label groups'
										: 'Add label groups to this project'
								)}
								maxDropdownHeight={200}
								data-testid="label-groups-select"
								dropdownPosition="top"
								styles={{
									item: { wordBreak: 'keep-all' },
									label: { width: '100%', display: 'flex' }
								}}
							/>
						</Stack>
					</Collapse>
				</Stack>
				<div className="form-actions">
					<Group align="center" position="right" py={8} px={16}>
						<Button variant="ghost" onClick={() => onCancelClick?.()}>
							Cancel
						</Button>
						<Button disabled={!isValid} loading={isCreatingProject} type="submit">
							Create Project
						</Button>
					</Group>
				</div>
			</form>
		</div>
	);
};

export default CreateProjectForm;
